<template>
  <b-card>
    <div class="mb-2">
      <!-- No selection mode: title placeholder + action buttons -->
      <div
        v-if="selectionMode === null"
        class="d-flex justify-content-between align-items-center"
      >
        <div />
        <div class="d-flex gap-1 align-items-center">
          <span
            id="head-to-head-button-wrapper"
            class="d-inline-block"
            tabindex="0"
          >
            <b-button
              variant="primary"
              :disabled="!isPremium"
              :style="!isPremium ? 'pointer-events: none;' : ''"
              @click="toggleSelectionMode('head-to-head')"
            >
              Head-to-Head
              <feather-icon
                icon="Edit2Icon"
                class="ml-50"
              />
              <feather-icon
                v-if="!isPremium"
                icon="LockIcon"
                class="ml-50"
              />
            </b-button>
          </span>
          <b-tooltip
            v-if="!isPremium"
            target="head-to-head-button-wrapper"
            triggers="hover"
            placement="top"
          >
            {{ premiumFeatureTooltip }}
          </b-tooltip>
          <hint-popup :id="30" />
          <span
            id="selection-record-button-wrapper"
            class="d-inline-block"
            tabindex="0"
          >
            <b-button
              variant="primary"
              :disabled="!isPremium"
              :style="!isPremium ? 'pointer-events: none;' : ''"
              @click="toggleSelectionMode('selection-record')"
            >
              Selection Record
              <feather-icon
                icon="Edit2Icon"
                class="ml-50"
              />
              <feather-icon
                v-if="!isPremium"
                icon="LockIcon"
                class="ml-50"
              />
            </b-button>
          </span>
          <b-tooltip
            v-if="!isPremium"
            target="selection-record-button-wrapper"
            triggers="hover"
            placement="top"
          >
            {{ premiumFeatureTooltip }}
          </b-tooltip>
          <hint-popup :id="31" />
        </div>
      </div>

      <!-- Active selection mode -->
      <template v-else>
        <!-- Desktop: single row layout -->
        <div class="d-none d-lg-flex justify-content-between align-items-center">
          <h4 class="mb-0">
            {{ selectionMode === 'selection-record' ? 'Selection Record' : 'Head-to-Head' }}
          </h4>
          <div class="d-flex gap-1 align-items-center">
            <b-button
              v-if="selectionMode === 'selection-record'"
              variant="outline-secondary"
              @click="toggleSelectAll"
            >
              {{ allProductsSelected ? 'Deselect All' : 'Select All' }}
            </b-button>
            <div class="d-flex flex-column mr-0 text-right align-self-center">
              <span style="line-height: 1.2; font-size: 1rem;">{{ selectionModeText }}</span>
              <span style="line-height: 1.2; font-size: 1rem;">{{ selectionCountText }}</span>
            </div>
            <span :id="`proceed-button-wrapper-${selectionMode}`">
              <b-button
                variant="success"
                :disabled="!isSelectionValid"
                @click="confirmSelection"
              >
                Proceed
              </b-button>
            </span>
            <b-tooltip
              v-if="!isSelectionValid && selectionWarningMessage"
              :key="selectionWarningMessage"
              :target="`proceed-button-wrapper-${selectionMode}`"
              triggers="hover focus"
              placement="top"
              variant="warning"
            >
              <span style="font-size: 1.1rem;">{{ selectionWarningMessage }}</span>
            </b-tooltip>
            <b-button
              variant="danger"
              @click="exitSelection"
            >
              Cancel
            </b-button>
          </div>
        </div>

        <!-- Mobile: stacked rows -->
        <div class="d-lg-none">
          <h4 class="mb-50">
            {{ selectionMode === 'selection-record' ? 'Selection Record' : 'Head-to-Head' }}
          </h4>
          <div class="d-flex align-items-center mb-50">
            <b-button
              v-if="selectionMode === 'selection-record'"
              variant="outline-secondary"
              size="sm"
              class="mr-1 flex-shrink-0"
              @click="toggleSelectAll"
            >
              {{ allProductsSelected ? 'Deselect All' : 'Select All' }}
            </b-button>
            <span>{{ selectionModeText }}</span>
          </div>
          <div class="mb-75">
            {{ selectionCountText }}
          </div>
          <div class="d-flex gap-1">
            <span
              :id="`proceed-button-wrapper-mobile-${selectionMode}`"
              class="flex-grow-1"
            >
              <b-button
                variant="success"
                block
                :disabled="!isSelectionValid"
                @click="confirmSelection"
              >
                Proceed
              </b-button>
            </span>
            <b-tooltip
              v-if="!isSelectionValid && selectionWarningMessage"
              :key="'mobile-' + selectionWarningMessage"
              :target="`proceed-button-wrapper-mobile-${selectionMode}`"
              triggers="hover focus"
              placement="top"
              variant="warning"
            >
              <span style="font-size: 1.1rem;">{{ selectionWarningMessage }}</span>
            </b-tooltip>
            <b-button
              variant="danger"
              class="flex-grow-1"
              @click="exitSelection"
            >
              Cancel
            </b-button>
          </div>
        </div>
      </template>
    </div>
    <!-- Live filter summary table, only shown in selection-record mode -->
    <div v-if="selectionMode === 'selection-record' && filterSummaryRows.length > 0" class="mb-2">
      <b-table
        :items="filterSummaryRows"
        :fields="filterSummaryFields"
        small
        bordered
        class="mb-0"
      >
        <template #cell(selectedValues)="data">
          <b-badge
            v-for="(value, i) in data.item.selectedValues"
            :key="i"
            variant="light-primary"
            class="mr-50 mb-25"
            pill
          >
            {{ value }}
          </b-badge>
        </template>
        <template #cell(productsDiscarded)="data">
          {{ data.item.productsDiscarded !== null ? data.item.productsDiscarded : '—' }}
        </template>
        <template #cell(productsRemaining)="data">
          {{ data.item.productsRemaining !== null ? data.item.productsRemaining : '—' }}
        </template>
      </b-table>
    </div>

    <!-- Table view: lg and above -->
    <b-table
      v-if="!products.error"
      class="d-none d-lg-block"
      responsive
      hover
      :items="products.data"
      :fields="fields"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
      :tbody-tr-class="getRowClass"
      @row-clicked="onRowClicked"
    >
      <template #cell(image)="data">
        <img
          loading="lazy"
          width="70"
          :src="`${baseURL}servethumbnail.cfm?id=${data.item.detailsId}`"
        >
      </template>
      <template #cell(closingdate)="data">
        <div class="d-flex flex-column gap-0.5">
          <span class="text-center">{{ formatIsoDate(data.item.closingDate) }}</span>
        </div>
      </template>
      <template #cell(provider)="data">
        <div class="d-flex flex-column gap-0.5">
          <span class="text-center">{{ data.item.provider }}</span>
          <span class="text-center">{{ data.item.productName }}</span>
          <span class="text-center">{{ data.item.counterParty }}</span>
        </div>
      </template>
      <template #cell(type)="data">
        <div class="d-flex flex-column gap-1">
          <span class="text-center">{{ data.item.type }}</span>
          <span class="text-center">{{ data.item.underlying }}</span>
          <span class="text-center">{{ data.item.length }}</span>
        </div>
      </template>
      <template #cell(capital)="data">
        <div class="d-flex flex-column gap-1">
          <span class="text-center">{{ data.item.capital }}</span>
          <span class="text-center">{{ data.item.aim }}</span>
          <span class="text-center">{{ data.item.vehicle }}</span>
        </div>
      </template>
      <template
        #cell(summary)="data"
      >
        <b-button
          :id="'summarypopover' + data.item.id"
          variant="flat-primary"
          class="btn-icon rounded-circle text-center"
          @click.stop.prevent
        >
          <feather-icon icon="InfoIcon" />
        </b-button>

        <b-popover
          :target="'summarypopover' + data.item.id"
          :triggers="['hover']"
          placement="auto"
          title="Summary"
          @show="loadSummaryBullets(data.item.reportNumber)"
          @hide="clearSummary"
        >
          <div v-if="loadingBullets || activeSummary.length === 0">
            <loading-container />
          </div>
          <div v-else>
            <p
              v-for="(item, index) in activeSummary"
              :key="item + index"
            >
              {{ index + 1 }}. {{ item }}
            </p>
          </div>
        </b-popover>
      </template>
      <template #cell(numportfolios)="data">
        <b-avatar
          variant="light-success"
        >
          <b-button
            v-if="isPremium"
            variant="outline-success"
            @click.stop.prevent="openPortfolioModal(data.item)"
          >
            <template v-if="portfolioCount(data.item) > 0">
              {{ portfolioCount(data.item).toString() }}
            </template>
            <feather-icon
              v-else
              size="18"
              icon="PlusIcon"
            />
          </b-button>
          <b-button
            v-else
            disabled
            variant="outline-success"
            @click.stop.prevent
          >
            <feather-icon
              icon="LockIcon"
              size="18"
            />
          </b-button>
        </b-avatar>
      </template>
      <template #cell(viewed)="data">
        <feather-icon
          v-if="data.item.viewed"
          icon="CheckIcon"
          size="20"
        />
      </template>
    </b-table>

    <!-- Card view: below lg -->
    <div
      v-if="!products.error"
      class="d-lg-none"
    >
      <div
        v-for="product in products.data"
        :key="product.reportNumber"
        class="product-card mb-1"
        :class="{ 'product-card--selected': selectionMode && selectedReportIds.includes(product.reportNumber) }"
      >
        <div
          class="product-card__header d-flex align-items-center"
          @click="onCardHeaderClicked(product)"
        >
          <img
            loading="lazy"
            width="50"
            class="mr-1 flex-shrink-0"
            :src="`${baseURL}servethumbnail.cfm?id=${product.detailsId}`"
          >
          <div class="flex-grow-1 overflow-hidden">
            <div class="product-card__title">
              {{ product.provider }}
            </div>
            <div class="product-card__subtitle">
              {{ product.productName }}
            </div>
          </div>
          <div class="d-flex align-items-center ml-1">
            <feather-icon
              v-if="product.viewed"
              icon="CheckIcon"
              size="16"
              class="text-success mr-75"
            />
            <feather-icon
              :icon="expandedCards[product.reportNumber] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="18"
              class="flex-shrink-0"
            />
          </div>
        </div>

        <div
          v-show="expandedCards[product.reportNumber]"
          class="product-card__body"
        >
          <div class="product-card__row">
            <span class="product-card__label">Report #</span>
            <span>{{ product.reportNumber }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Name</span>
            <span>{{ product.productName }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Provider</span>
            <span>{{ product.provider }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Closing Date</span>
            <span>{{ formatIsoDate(product.closingDate) }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Counterparty</span>
            <span>{{ product.counterParty }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Type</span>
            <span>{{ product.type }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Underlying</span>
            <span>{{ product.underlying }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Length</span>
            <span>{{ product.length }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Capital</span>
            <span>{{ product.capital }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Aim</span>
            <span>{{ product.aim }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Vehicle</span>
            <span>{{ product.vehicle }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Score</span>
            <span>{{ product.score }}</span>
          </div>
          <div class="product-card__row">
            <span class="product-card__label">Risk</span>
            <span>{{ product.risk }}</span>
          </div>
          <div class="product-card__actions">
            <b-button
              :id="'card-summarypopover-' + product.reportNumber"
              size="sm"
              variant="outline-primary"
              class="flex-grow-1"
              @click.stop
            >
              <feather-icon icon="InfoIcon" class="mr-25" />
              Summary
            </b-button>
            <b-button
              size="sm"
              variant="primary"
              class="flex-grow-1"
              @click.stop="navigateToReport(product)"
            >
              View Report
            </b-button>
            <b-button
              size="sm"
              variant="outline-success"
              class="flex-grow-1"
              :disabled="!isPremium"
              @click.stop.prevent="openPortfolioModal(product)"
            >
              <template v-if="isPremium && portfolioCount(product) > 0">
                {{ portfolioButtonText(product) }}
              </template>
              <template v-else-if="isPremium">
                <feather-icon
                  icon="PlusIcon"
                  class="mr-25"
                />
                Portfolio
              </template>
              <template v-else>
                <feather-icon
                  icon="LockIcon"
                  class="mr-25"
                />
                Portfolio
              </template>
            </b-button>
            <b-popover
              :target="'card-summarypopover-' + product.reportNumber"
              :triggers="['click']"
              placement="auto"
              title="Summary"
              @show="loadSummaryBullets(product.reportNumber)"
              @hide="clearSummary"
            >
              <div v-if="loadingBullets || activeSummary.length === 0">
                <loading-container />
              </div>
              <div v-else>
                <p
                  v-for="(item, index) in activeSummary"
                  :key="item + index"
                >
                  {{ index + 1 }}. {{ item }}
                </p>
              </div>
            </b-popover>
          </div>
        </div>
      </div>
    </div>

    <div v-if="products.loading">
      <loading-container />
    </div>
    <no-data-container
      v-else-if="products.data.length < 1"
      message="No Products Matched Your Search"
    />

    <!-- Add Product to Portfolio Modal -->
    <b-modal
      v-model="addProductToPortfolio.show"
      centered
      title="Add Product To Portfolio"
      hide-footer
    >
      <add-product-to-portfolio-modal-content
        v-if="addProductToPortfolio.product"
        :product="addProductToPortfolio.product"
        @portfolioUpdated="onProductAddedToPortfolio"
      />
    </b-modal>

    <!-- Selection Mode Helper -->
    <transition name="slide-up">
      <div
        v-if="false"
        class="selection-helper"
      >
        <div class="selection-helper-content">
          <feather-icon
            icon="InfoIcon"
            size="20"
            class="mr-75"
          />
          <div class="selection-helper-text-container">
            <div class="selection-helper-main">
              Click on reports to select/deselect
            </div>
            <div class="selection-helper-status">
              <span :class="{ 'text-success': isSelectionValid, 'text-warning': !isSelectionValid }">
                {{ selectionStatusText }}
              </span>
            </div>
            <div
              v-if="isSelectionValid"
              class="selection-helper-action"
            >
              Press <strong>Proceed</strong> when done
            </div>
          </div>
        </div>
      </div>
    </transition>
  </b-card>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BModal,
  BPopover,
  BTable,
  BTooltip,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import AddProductToPortfolioModalContent from '@/views/components/AddProductToPortfolioModalContent.vue'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import HintPopup from '@/views/components/HintPopup.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import {
  formatIsoDate,
  formatNumberTextMaxDecimalPlaces,
} from '@/utils/formatters'

export default {
  components: {
    AddProductToPortfolioModalContent,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BModal,
    BPopover,
    BTable,
    BTooltip,
    LoadingContainer,
    HintPopup,
    NoDataContainer,
  },
  props: {
    search: {
      type: String,
      default: '',
    },
    products: {
      type: Object,
      required: true,
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
    filterDisplay: {
      type: Array,
      default: () => [],
    },
    constraints: {
      type: Object,
      default: () => ({}),
    },
    filterInteractionOrder: {
      type: Array,
      default: () => [],
    },
    filterSequentialCounts: {
      type: Object,
      default: () => ({}),
    },
    totalProductCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      selectionMode: null, // Can be null, 'head-to-head', or 'selection-record'
      selectedReportIds: [],
      seenProductMap: {}, // reportNumber -> product object, all products seen during selection-record mode
      filterSummaryFields: [
        { key: 'index', label: '#', thStyle: { width: '4%' } },
        { key: 'label', label: 'Filter Choice', thStyle: { width: '20%' } },
        { key: 'selectedValues', label: 'Selection', thStyle: { width: '35%' } },
        { key: 'productsDiscarded', label: 'Products Discarded', thStyle: { width: '20%' } },
        { key: 'productsRemaining', label: 'Products Remaining', thStyle: { width: '21%' } },
      ],
      expandedCards: {},
      addProductToPortfolio: {
        show: false,
        product: null,
      },
      cachedSummaryBullets: {},
      activeSummary: [],
      loadingBullets: true,
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      fields: [
        {
          key: 'image',
          label: ' ',
          sortable: false,
          thStyle: { width: '5%' },
        },
        {
          key: 'closingDate',
          label: 'Closing Date',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'reportNumber',
          label: 'Report Number',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'provider',
          label: 'Provider Product name counter party',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'type',
          label: 'Type Underlying Length',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'capital',
          label: 'Capital Aim Vehicle',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'summary',
          label: 'Summary',
          sortable: false,
          thStyle: { width: '15%' },
          tdClass: 'text-center',
        },
        {
          key: 'score',
          label: 'Score',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'risk',
          label: 'Risk',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'numportfolios',
          label: 'Port-folios',
          thStyle: { width: '8%' },
          tdClass: 'text-center',
        },
        {
          key: 'viewed',
          label: 'Viewed',
          sortable: true,
          thStyle: { width: '15%' },
          tdClass: 'text-center',
        },
      ],
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'isPremium',
    ]),
    premiumFeatureTooltip() {
      if (!this.isLoggedIn) {
        return 'Upgrade to Premium to use this feature'
      }
      return 'Upgrade to Premium to use this feature'
    },
    allProductsSelected() {
      return this.products.data.length > 0
        && this.products.data.every(product => this.selectedReportIds.includes(product.reportNumber))
    },
    selectionModeText() {
      if (this.selectionMode === 'head-to-head') {
        return 'Selecting reports for comparison...'
      }
      if (this.selectionMode === 'selection-record') {
        return 'Creating a selection record...'
      }
      return ''
    },
    selectionCountText() {
      const count = this.selectedReportIds.length
      const productText = count === 1 ? 'product' : 'products'

      if (this.selectionMode === 'head-to-head') {
        return `${count} ${productText} selected, 2-4 required`
      }
      if (this.selectionMode === 'selection-record') {
        return `${count} ${productText} selected, 1+ required`
      }
      return ''
    },
    isSelectionValid() {
      if (this.products.loading || this.products.error) return false
      const count = this.selectedReportIds.length

      if (this.selectionMode === 'head-to-head') {
        return count >= 2 && count <= 4
      }
      if (this.selectionMode === 'selection-record') {
        return count >= 1
      }
      return false
    },
    selectionWarningMessage() {
      const count = this.selectedReportIds.length

      if (this.selectionMode === 'head-to-head') {
        if (count < 2) return 'Select between 2 and 4 products'
        if (count > 4) return 'Maximum 4 products allowed'
      }
      if (this.selectionMode === 'selection-record') {
        if (count < 1) return 'Select one or more products'
      }
      return ''
    },
    selectionStatusText() {
      const count = this.selectedReportIds.length
      const productText = count === 1 ? 'product' : 'products'

      if (this.selectionMode === 'head-to-head') {
        if (count < 2) {
          return 'Need minimum 2 products'
        }
        if (count > 4) {
          return 'Maximum 4 products allowed'
        }
        return `${count} ${productText} selected`
      }

      if (this.selectionMode === 'selection-record') {
        if (count < 1) {
          return 'Select at least 1 product'
        }
        return `${count} ${productText} selected`
      }

      return ''
    },
    filterSummaryRows() {
      if (!this.filterData || !this.filterDisplay) return []
      const rows = []
      let remaining = this.totalProductCount || null

      if (this.search) {
        const productsRemaining = this.filterSequentialCounts.search !== undefined
          ? this.filterSequentialCounts.search : null
        rows.push({
          index: 1,
          label: 'Search',
          selectedValues: [this.search],
          productsDiscarded: productsRemaining !== null && remaining !== null
            ? Math.max(0, remaining - productsRemaining) : null,
          productsRemaining,
        })
        remaining = productsRemaining
      }

      const activeKeys = Object.keys(this.filterData).filter(k => this.filterData[k] && this.filterData[k].length > 0)
      const unordered = activeKeys.filter(k => !this.filterInteractionOrder.includes(k))
      const ordered = this.filterInteractionOrder.filter(k => activeKeys.includes(k))
      const sortedKeys = [...unordered, ...ordered]

      sortedKeys.forEach(categoryKey => {
        const selectedValues = this.filterData[categoryKey]

        const category = this.filterDisplay.find(cat => cat.categoryKey === categoryKey)
        if (!category) return

        const isSlider = category.slider !== undefined
        let displayValues = []

        if (isSlider) {
          const sortedItems = selectedValues
            .map(v => category.items.find(i => String(i.value) === String(v)))
            .filter(item => item)
            .sort((a, b) => category.items.indexOf(a) - category.items.indexOf(b))
          if (sortedItems.length === 0) return
          const minLabel = formatNumberTextMaxDecimalPlaces(sortedItems[0].label)
          const maxLabel = formatNumberTextMaxDecimalPlaces(sortedItems[sortedItems.length - 1].label)
          displayValues = minLabel === maxLabel ? [minLabel] : [`${minLabel} - ${maxLabel}`]
        } else {
          displayValues = selectedValues.map(value => {
            const item = category.items.find(i => String(i.value) === String(value))
            return item ? item.label : value
          })
        }

        const productsRemaining = this.filterSequentialCounts[categoryKey] !== undefined
          ? this.filterSequentialCounts[categoryKey]
          : null
        const productsDiscarded = productsRemaining !== null && remaining !== null
          ? Math.max(0, remaining - productsRemaining)
          : null
        remaining = productsRemaining !== null ? productsRemaining : remaining

        rows.push({
          index: rows.length + 1,
          label: category.categoryLabel,
          selectedValues: displayValues,
          productsDiscarded,
          productsRemaining,
        })
      })

      return rows
    },
  },
  watch: {
    search() {
      this.selectedReportIds = []
      this.seenProductMap = {}
    },
    filterData: {
      handler() {
        if (this.selectionMode) {
          this.selectedReportIds = []
        }
      },
      deep: true,
    },
    constraints: {
      handler() {
        if (this.selectionMode) {
          this.selectedReportIds = []
        }
      },
      deep: true,
    },
    'products.data': {
      handler(newProducts) {
        if (this.selectionMode === 'selection-record' && newProducts) {
          const newIds = new Set(newProducts.map(p => p.reportNumber))

          // Remove products no longer in the filtered list that were never selected
          Object.keys(this.seenProductMap).forEach(reportNumber => {
            const id = Number(reportNumber) || reportNumber
            if (!newIds.has(id) && !newIds.has(String(id)) && !this.selectedReportIds.includes(id) && !this.selectedReportIds.includes(String(id))) {
              this.$delete(this.seenProductMap, reportNumber)
            }
          })

          // Add any new products not yet seen
          newProducts.forEach(product => {
            if (!this.seenProductMap[product.reportNumber]) {
              this.$set(this.seenProductMap, product.reportNumber, product)
            }
          })
        }
      },
      deep: true,
    },
  },
  methods: {
    formatIsoDate,
    toggleSelectionMode(mode) {
      if (this.selectionMode === mode) {
        this.selectionMode = null
        this.selectedReportIds = []
        this.seenProductMap = {}
        if (mode === 'selection-record') {
          sessionStorage.removeItem('selectionRecordData')
        }
      } else {
        this.selectionMode = mode
        this.selectedReportIds = []
        this.seenProductMap = {}
        if (mode === 'selection-record') {
          sessionStorage.removeItem('selectionRecordData')
          // Snapshot all currently visible products as the initial seen set
          this.products.data.forEach(product => {
            this.$set(this.seenProductMap, product.reportNumber, product)
          })
        }
      }
    },
    toggleSelectAll() {
      this.selectedReportIds = this.allProductsSelected
        ? []
        : this.products.data.map(product => product.reportNumber)
    },
    confirmSelection() {
      if (this.selectionMode === 'head-to-head') {
        this.$router.push({
          name: 'head-to-head',
          params: {
            reportNumber: this.selectedReportIds,
          },
        })
      } else if (this.selectionMode === 'selection-record') {
        const selectedProducts = this.products.data.filter(
          product => this.selectedReportIds.includes(product.reportNumber),
        )

        // Excluded = seen during selection mode but never selected
        const excludedProducts = Object.values(this.seenProductMap).filter(
          product => !this.selectedReportIds.includes(product.reportNumber),
        )
        // Reorder filterData keys so the most recently interacted category is last
        const orderedFilterData = {}
        const unordered = Object.keys(this.filterData).filter(k => !this.filterInteractionOrder.includes(k))
        const ordered = this.filterInteractionOrder.filter(k => Object.prototype.hasOwnProperty.call(this.filterData, k))
        const allKeys = [...unordered, ...ordered]
        allKeys.forEach(k => { orderedFilterData[k] = this.filterData[k] })

        // Save all data to sessionStorage before navigation
        const selectionRecordData = {
          search: this.search,
          reportIds: this.selectedReportIds,
          products: selectedProducts,
          excludedReportIds: excludedProducts.map(p => p.reportNumber),
          excludedProducts,
          filterData: orderedFilterData,
          filterDisplay: this.filterDisplay,
          filterSequentialCounts: this.filterSequentialCounts,
          totalProductCount: this.totalProductCount,
          constraints: this.constraints,
        }
        sessionStorage.setItem('selectionRecordData', JSON.stringify(selectionRecordData))
        this.addSelectionRecordGenerateHit(orderedFilterData)

        this.$router.push({
          name: 'selection-record',
        })
      }
    },
    exitSelection() {
      const wasSelectionRecord = this.selectionMode === 'selection-record'
      this.selectionMode = null
      this.selectedReportIds = []
      this.seenProductMap = {}
      // Clear sessionStorage if we were in selection-record mode
      if (wasSelectionRecord) {
        sessionStorage.removeItem('selectionRecordData')
      }
    },
    onRowClicked(row) {
      if (this.selectionMode) {
        const selectedIndex = this.selectedReportIds.indexOf(row.reportNumber)
        if (selectedIndex > -1) {
          this.selectedReportIds.splice(selectedIndex, 1)
        } else {
          this.selectedReportIds.push(row.reportNumber)
        }
      } else {
        // Normal mode: navigate to product report
        this.$router.push({
          name: 'product-report',
          params: {
            reportNumber: row.reportNumber,
          },
        })
      }
    },
    getRowClass(item) {
      if (this.selectionMode && item && this.selectedReportIds.includes(item.reportNumber)) {
        return 'row-selected'
      }
      return ''
    },
    loadSummaryBullets(reportNumber) {
      this.loadingBullets = true
      if (this.cachedSummaryBullets[reportNumber]) {
        this.activeSummary = this.cachedSummaryBullets[reportNumber]
        this.loadingBullets = false
        return
      }

      this.$http
        .get('productfilter.cfc?method=getTooltip', {
          params: {
            reportnumber: reportNumber,
          },
        })
        .then(response => {
          this.cachedSummaryBullets[reportNumber] = response.data
          this.activeSummary = response.data
          this.loadingBullets = false
        })
    },
    clearSummary() {
      this.activeSummary = []
    },
    onCardHeaderClicked(product) {
      if (this.selectionMode) {
        this.onRowClicked(product)
      } else {
        this.$set(this.expandedCards, product.reportNumber, !this.expandedCards[product.reportNumber])
      }
    },
    navigateToReport(product) {
      this.$router.push({
        name: 'product-report',
        params: { reportNumber: product.reportNumber },
      })
    },
    portfolioCount(product) {
      return Number(product && product.numportfolios ? product.numportfolios : 0)
    },
    portfolioButtonText(product) {
      const count = this.portfolioCount(product)
      return `${count} Portfolio${count === 1 ? '' : 's'}`
    },
    openPortfolioModal(product) {
      if (!this.isPremium) {
        return
      }

      this.addProductToPortfolio.product = {
        ...product,
        productid: product.reportNumber,
        productname: product.productName,
      }
      this.addProductToPortfolio.show = true
    },
    onProductAddedToPortfolio(portfolioIds) {
      this.addProductToPortfolio.show = false

      this.$bvToast.toast('Product Portfolio Updated', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })

      this.$store.dispatch('app/updatePortfolios')
      this.$store.commit('app/SET_PORTFOLIO_UPDATED', true)
      this.$store.commit('app/ADD_UPDATED_PORTFOLIOS', portfolioIds)
      this.$emit('portfolio-updated')
    },
    addSelectionRecordGenerateHit(filterData) {
      this.$http
        .get('hits.cfc', {
          params: {
            method: 'addProductListHit',
            hitname: 'selection_record_generate',
            filterstate: JSON.stringify(filterData),
          },
        })
    },
  },
}
</script>

<style scoped>
.gap-0\.5 {
  gap: 0.5rem;
}
.gap-1 {
  gap: 1rem;
}

.product-card {
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  overflow: hidden;
}

.product-card--selected {
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.35);
}

.product-card__header {
  padding: 0.75rem 1rem;
  cursor: pointer;
  user-select: none;
}

.product-card--selected .product-card__header {
  background-color: #1a73e8;
  color: white;
}

.product-card__title {
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-card__subtitle {
  font-size: 0.8rem;
  opacity: 0.75;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-card__body {
  padding: 0.75rem 1rem;
  border-top: 1px solid #e0e0e0;
}

.product-card__row {
  display: flex;
  justify-content: space-between;
  padding: 0.3rem 0;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.product-card__row:last-of-type {
  border-bottom: none;
}

.product-card__label {
  font-weight: 600;
  color: #6e6b7b;
  flex-shrink: 0;
  margin-right: 0.5rem;
}

.product-card__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
</style>

<style>
.row-selected {
  background-color: #1a73e8 !important;
  color: white !important;
}

.row-selected td {
  color: white !important;
}

.row-selected:hover {
  background-color: #1a73e8 !important;
  color: white !important;
}

.row-selected:hover td {
  color: white !important;
}

.selection-helper {
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  z-index: 1000;
  background: linear-gradient(135deg, #1a73e8 0%, #9e95f5 100%);
  padding: 1rem 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 4px 12px rgba(26, 115, 232, 0.4);
  animation: pulse 2s ease-in-out infinite;
  max-width: 320px;
}

.selection-helper-content {
  display: flex;
  align-items: flex-start;
  color: white;
}

.selection-helper-text-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.selection-helper-main {
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.3;
}

.selection-helper-status {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.3;
}

.selection-helper-status .text-success {
  color: #a0f5c5 !important;
}

.selection-helper-status .text-warning {
  color: #ffd17a !important;
}

.selection-helper-action {
  font-size: 0.85rem;
  opacity: 0.95;
  line-height: 1.3;
  margin-top: 0.1rem;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100px);
  opacity: 0;
}

/* Pulse animation */
@keyframes pulse {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(26, 115, 232, 0.4);
  }
  50% {
    box-shadow: 0 6px 20px rgba(26, 115, 232, 0.6);
  }
}

/* Dark mode card styles */
.dark-layout .product-card {
  border-color: #3b4253;
}

.dark-layout .product-card__body {
  border-top-color: #3b4253;
}

.dark-layout .product-card__row {
  border-bottom-color: rgba(255,255,255,0.05);
}

.dark-layout .product-card__label {
  color: #b4b7bd;
}

/* Responsive */
@media (max-width: 768px) {
  .selection-helper {
    left: 1rem;
    bottom: 1rem;
    padding: 0.75rem 1rem;
    max-width: 280px;
  }

  .selection-helper-main {
    font-size: 0.85rem;
  }

  .selection-helper-status {
    font-size: 0.8rem;
  }

  .selection-helper-action {
    font-size: 0.75rem;
  }
}
</style>
